/**
 * Location Services for TabPicker Geolocation Features
 * 
 * Provides privacy-focused location services for game template discovery.
 * Implements context-driven permission prompts and location fuzzing for user privacy.
 * 
 * Key Features:
 * - Context-aware location permission requests
 * - Location fuzzing for privacy protection
 * - Graceful fallback when permissions denied
 * - Accuracy validation and error handling
 * - User preference storage
 * 
 * @module LocationServices
 */

export interface LocationContext {
  purpose: string;
  benefits: readonly string[];
  fallback: string;
}

export interface GeolocationCoords {
  latitude: number;
  longitude: number;
  accuracy: number;
}

export interface FuzzedLocation {
  latitude: number;
  longitude: number;
  originalAccuracy: number;
  fuzzingRadius: number;
}

export interface LocationPermissionPreference {
  granted: boolean;
  timestamp: number;
  context: string;
}

/**
 * Location Services Class
 * 
 * Handles all geolocation-related functionality with privacy-by-design principles.
 */
export class LocationService {
  private static readonly STORAGE_KEY_PREFERENCE = 'tabpicker_location_preference';
  private static readonly STORAGE_KEY_DECLINED = 'tabpicker_location_declined';
  private static readonly MIN_ACCURACY_METERS = 500000; // 500km max uncertainty - very permissive for mobile testing
  private static readonly DEFAULT_FUZZING_RADIUS = 1000; // 1km fuzzing radius
  private static readonly PREFERENCE_EXPIRY_DAYS = 30;

  /**
   * Request user location with context-driven prompt
   * 
   * @param context - Context explaining why location is needed
   * @returns Promise resolving to GeolocationPosition or null if denied
   */
  static async requestLocation(context: LocationContext): Promise<GeolocationPosition | null> {
    try {
      // Check if geolocation is supported
      if (!navigator.geolocation) {
        console.warn('Geolocation not supported by this browser');
        return null;
      }

      // Note: We removed the hasUserDeclinedLocation check here because it was creating
      // a catch-22 where header blocking errors were interpreted as user declines,
      // preventing the permission prompt from ever appearing again

      // Request location with mobile-optimized timeout
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          resolve,
          reject,
          {
            enableHighAccuracy: true,
            timeout: 30000, // 30 second timeout for mobile GPS lock
            maximumAge: 300000, // 5 minute cache
          }
        );
      });

      // Validate location accuracy
      if (!this.validateLocationAccuracy(position)) {
        console.warn('Location accuracy too low:', position.coords.accuracy);
        return null;
      }

      // Store successful permission grant
      this.storeLocationPreference({
        granted: true,
        timestamp: Date.now(),
        context: context.purpose
      });

      return position;

    } catch (error) {
      console.log('Location request failed:', error);
      
      // Handle all geolocation errors properly
      if (error instanceof GeolocationPositionError) {
        switch (error.code) {
          case GeolocationPositionError.PERMISSION_DENIED:
            console.log('Location permission denied');
            // Only store as declined if we can verify the user actually denied it
            // (not just blocked by headers or other technical issues)
            // We check if the Permissions API is available and shows "denied"
            if ('permissions' in navigator) {
              navigator.permissions.query({ name: 'geolocation' }).then(result => {
                if (result.state === 'denied') {
                  console.log('User has explicitly denied location permission');
                  this.storeLocationDeclined(context.purpose);
                } else {
                  console.log('Permission denied but state is:', result.state, '- likely a technical issue');
                }
              });
            }
            break;
          case GeolocationPositionError.POSITION_UNAVAILABLE:
            console.log('Location position unavailable (GPS/network issue)');
            break;
          case GeolocationPositionError.TIMEOUT:
            console.log('Location request timed out (mobile GPS lock failed)');
            break;
          default:
            console.log('Unknown geolocation error:', error.code);
        }
      } else {
        console.log('Non-geolocation error:', error);
      }
      
      return null;
    }
  }

  /**
   * Fuzz location coordinates for privacy protection
   * 
   * Adds random offset within specified radius to protect user privacy
   * while maintaining sufficient accuracy for proximity matching.
   * 
   * @param coords - Original coordinates
   * @param fuzzingRadius - Fuzzing radius in meters (default: 1000m)
   * @returns Fuzzed coordinates with metadata
   */
  static fuzzLocation(coords: GeolocationCoords, fuzzingRadius = this.DEFAULT_FUZZING_RADIUS): FuzzedLocation {
    // Generate random angle and distance within fuzzing radius
    const randomAngle = Math.random() * 2 * Math.PI;
    const randomDistance = Math.random() * fuzzingRadius;
    
    // Convert to lat/lng offsets
    // 1 degree latitude ≈ 111,320 meters
    // 1 degree longitude ≈ 111,320 * cos(latitude) meters
    const latOffset = randomDistance * Math.cos(randomAngle) / 111320;
    const lngOffset = randomDistance * Math.sin(randomAngle) / (111320 * Math.cos(coords.latitude * Math.PI / 180));
    
    return {
      latitude: coords.latitude + latOffset,
      longitude: coords.longitude + lngOffset,
      originalAccuracy: coords.accuracy,
      fuzzingRadius
    };
  }

  /**
   * Validate location accuracy is sufficient for proximity matching
   * 
   * @param position - Geolocation position to validate
   * @returns True if accuracy is acceptable
   */
  static validateLocationAccuracy(position: GeolocationPosition): boolean {
    const accuracy = position.coords.accuracy;
    return accuracy <= this.MIN_ACCURACY_METERS;
  }

  /**
   * Check if user has previously declined location sharing
   * 
   * @returns True if user has declined and preference hasn't expired
   */
  static hasUserDeclinedLocation(): boolean {
    try {
      const declined = localStorage.getItem(this.STORAGE_KEY_DECLINED);
      if (!declined) return false;

      const declinedData = JSON.parse(declined);
      const expiryTime = declinedData.timestamp + (this.PREFERENCE_EXPIRY_DAYS * 24 * 60 * 60 * 1000);
      
      // Check if preference has expired
      if (Date.now() > expiryTime) {
        localStorage.removeItem(this.STORAGE_KEY_DECLINED);
        return false;
      }

      return true;
    } catch {
      return false;
    }
  }

  /**
   * Store user location preference
   * 
   * @param preference - Location permission preference
   */
  static storeLocationPreference(preference: LocationPermissionPreference): void {
    try {
      localStorage.setItem(this.STORAGE_KEY_PREFERENCE, JSON.stringify(preference));
    } catch (error) {
      console.warn('Failed to store location preference:', error);
    }
  }

  /**
   * Store user location decline for better UX
   * 
   * @param context - Context when location was declined
   */
  static storeLocationDeclined(context: string): void {
    try {
      const declinedData = {
        timestamp: Date.now(),
        context
      };
      localStorage.setItem(this.STORAGE_KEY_DECLINED, JSON.stringify(declinedData));
    } catch (error) {
      console.warn('Failed to store location decline:', error);
    }
  }

  /**
   * Clear stored location preferences (for privacy controls)
   */
  static clearLocationPreferences(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY_PREFERENCE);
      localStorage.removeItem(this.STORAGE_KEY_DECLINED);
    } catch (error) {
      console.warn('Failed to clear location preferences:', error);
    }
  }

  /**
   * Get stored location preference
   * 
   * @returns Stored preference or null if none exists
   */
  static getLocationPreference(): LocationPermissionPreference | null {
    try {
      const preference = localStorage.getItem(this.STORAGE_KEY_PREFERENCE);
      if (!preference) return null;

      const data = JSON.parse(preference);
      const expiryTime = data.timestamp + (this.PREFERENCE_EXPIRY_DAYS * 24 * 60 * 60 * 1000);
      
      // Check if preference has expired
      if (Date.now() > expiryTime) {
        localStorage.removeItem(this.STORAGE_KEY_PREFERENCE);
        return null;
      }

      return data;
    } catch {
      return null;
    }
  }

  /**
   * Calculate distance between two coordinates in meters
   * 
   * Uses Haversine formula for accurate distance calculation.
   * 
   * @param lat1 - First latitude
   * @param lon1 - First longitude  
   * @param lat2 - Second latitude
   * @param lon2 - Second longitude
   * @returns Distance in meters
   */
  static calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371e3; // Earth radius in meters
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;

    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

    return R * c;
  }

  /**
   * Format distance for user display
   * 
   * @param meters - Distance in meters
   * @returns Formatted distance string
   */
  static formatDistance(meters: number): string {
    if (meters < 1000) {
      return `${Math.round(meters)}m`;
    } else if (meters < 10000) {
      return `${(meters / 1000).toFixed(1)}km`;
    } else {
      return `${Math.round(meters / 1000)}km`;
    }
  }
}

/**
 * Context definitions for different location use cases
 */
export const LocationContexts = {
  GAME_CREATION: {
    purpose: "Share this game as a template for other local players",
    benefits: [
      "Help other TabPicker users discover your game template",
      "Save other players time on game setup",
      "Build local gaming communities"
    ],
    fallback: "You can still save your game privately without location sharing"
  },
  
  TEMPLATE_DISCOVERY: {
    purpose: "Find game templates near you",
    benefits: [
      "Discover game templates from nearby users",
      "Save time on manual game setup",
      "Find locally popular game configurations"
    ],
    fallback: "Browse all templates without location filtering"
  },
  
  SETTINGS_UPDATE: {
    purpose: "Update your location sharing preferences",
    benefits: [
      "Control how your location is used",
      "Set discovery radius preferences",
      "Manage template sharing settings"
    ],
    fallback: "Use default global settings without location features"
  }
} as const;

export type LocationContextType = keyof typeof LocationContexts;